import { z } from 'zod';
import { api } from '../../../lib/api/client';

// S17 · the activity stream (07 §5): `GET /internal/activity` (filters,
// `before` pages back, `after` catches up with the live stream),
// `GET /internal/activity/facets` (the pickers), `GET /internal/activity/:id`
// (the drawer, with signed blob links). Mirrored from
// apps/api/src/modules/activity/activity.service.ts. Relative imports only:
// the API's integration spec imports this file at run time.

/** S17 «Attore»: the API's actor groups (`actor_group`). */
export const ACTOR_GROUPS = ['extension', 'api', 'worker', 'telegram', 'dokless', 'user'] as const;
export type ActorGroup = (typeof ACTOR_GROUPS)[number];
export const LEVELS = ['info', 'warn', 'error'] as const;
export type Level = (typeof LEVELS)[number];

export const activityRowSchema = z.looseObject({
  id: z.string(),
  at: z.string(),
  actor: z.string(),
  kind: z.string(),
  level: z.string(),
  shipment_id: z.string().nullable(),
  carrier_id: z.string().nullable(),
  job_id: z.string().nullable(),
  read_id: z.string().nullable(),
  summary: z.string(),
  details: z.unknown().optional(),
  request_ref: z.string().nullable(),
  response_ref: z.string().nullable(),
  duration_ms: z.number().nullable(),
  shipment_ref: z.string().nullish(),
  carrier_code: z.string().nullish(),
});
export type ActivityRow = z.infer<typeof activityRowSchema>;

export const activityPageSchema = z.looseObject({
  items: z.array(activityRowSchema),
  next_before: z.string().nullable(),
});
export type ActivityPage = z.infer<typeof activityPageSchema>;

const blobLink = z
  .looseObject({ key: z.string(), url: z.string().nullable(), pruned: z.boolean() })
  .nullable();

export const activityDetailSchema = activityRowSchema.extend({ request: blobLink, response: blobLink });
export type ActivityDetail = z.infer<typeof activityDetailSchema>;

export const activityFacetsSchema = z.looseObject({
  kinds: z.array(z.string()),
  carriers: z.array(z.looseObject({ id: z.string(), code: z.string(), name: z.string() })),
});
export type ActivityFacets = z.infer<typeof activityFacetsSchema>;

/** The live line on `GET /internal/events?types=activity`. */
export const activityEventSchema = z.looseObject({ type: z.literal('activity'), id: z.string() });

/** The URL state of S17 (route `validateSearch`). */
export const streamSearchSchema = z.object({
  actor: z.enum(ACTOR_GROUPS).optional().catch(undefined),
  level: z.enum(LEVELS).optional().catch(undefined),
  carrier: z.coerce.string().max(64).optional().catch(undefined),
  shipment: z.coerce.string().max(64).optional().catch(undefined),
  kind: z.coerce.string().max(48).optional().catch(undefined),
  q: z.coerce.string().max(128).optional().catch(undefined),
  /** The row open in the drawer. */
  row: z.coerce
    .string()
    .regex(/^\d{1,19}$/)
    .optional()
    .catch(undefined),
});
export type StreamSearch = z.infer<typeof streamSearchSchema>;
export type StreamFilters = Omit<StreamSearch, 'row'>;

export const PAGE_SIZE = 50;

/**
 * The list query. Never sends `since`: the shell's sidebar badge asks
 * `?level=error&since=…`, so the two are told apart by it.
 */
export function listPath(filters: StreamFilters, cursor: { before?: string; after?: string } = {}): string {
  const params = new URLSearchParams();
  if (filters.actor) params.set('actor_group', filters.actor);
  if (filters.level) params.set('level', filters.level);
  if (filters.carrier) params.set('carrier', filters.carrier);
  if (filters.shipment) params.set('shipment', filters.shipment);
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.q?.trim()) params.set('q', filters.q.trim());
  if (cursor.before) params.set('before', cursor.before);
  if (cursor.after) params.set('after', cursor.after);
  params.set('limit', String(cursor.after ? 100 : PAGE_SIZE));
  return `/internal/activity?${params.toString()}`;
}

export const streamKeys = {
  all: ['activity', 'stream'] as const,
  list: (filters: StreamFilters) => ['activity', 'stream', 'list', filters] as const,
  facets: ['activity', 'stream', 'facets'] as const,
  row: (id: string) => ['activity', 'stream', 'row', id] as const,
};

export const streamApi = {
  page: (filters: StreamFilters, cursor: { before?: string; after?: string } = {}, signal?: AbortSignal) =>
    api(listPath(filters, cursor), { schema: activityPageSchema, ...(signal ? { signal } : {}) }),
  facets: () => api('/internal/activity/facets', { schema: activityFacetsSchema }),
  row: (id: string) => api(`/internal/activity/${encodeURIComponent(id)}`, { schema: activityDetailSchema }),
};
